/**
 * RoundaboutTriggerCard — injected inline next to a homework lesson when
 * the lesson title matches a roundabout-related trigger.
 *
 * Owner spec: ONE understanding point at a time. Card opens collapsed —
 * tappable to expand reasoning. "Got it" snoozes 7 days.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ChevronDown, ChevronUp, Lightbulb, Eye, Target } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RoundaboutTriggerCard({ lessonTitle, skill }) {
  const [card, setCard] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!lessonTitle && !skill) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (lessonTitle) params.set("lesson_title", lessonTitle);
        if (skill) params.set("skill", skill);
        const { data } = await axios.get(
          `${API}/api/roundabouts/triggered-card?${params.toString()}`,
          { withCredentials: true }
        );
        if (!cancelled && data?.card) setCard(data.card);
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, [lessonTitle, skill]);

  async function gotIt() {
    if (!card) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/roundabouts/me/understood`,
        { card_id: card.id },
        { withCredentials: true }
      );
      toast.success("Got it — back in 7 days as a refresher.");
      (data?.newly_earned || []).forEach((b) => {
        toast.success(`🏅 Badge earned: ${b.label}`, { description: b.tagline });
      });
      setHidden(true);
    } catch {
      toast.error("Couldn't save. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (!card || hidden) return null;

  return (
    <div
      data-testid={`roundabout-trigger-${card.id}`}
      className="rounded-xl border border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/15 overflow-hidden"
    >
      <button
        type="button"
        data-testid={`roundabout-trigger-toggle-${card.id}`}
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-3 py-2.5 flex items-center gap-2.5 text-left hover:bg-emerald-100/60 dark:hover:bg-emerald-900/25 transition-colors"
      >
        <div className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-400">Roundabout insight</p>
          <p className="text-sm font-bold text-slate-900 dark:text-white leading-snug truncate">{card.title}</p>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-emerald-700 dark:text-emerald-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-emerald-700 dark:text-emerald-400 flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-emerald-200 dark:border-emerald-800">
          <Fact icon={Lightbulb} label="Why" body={card.why} />
          <Fact icon={Eye} label="Look for" body={card.look_for} />
          <Fact icon={Target} label="Cue" body={card.cue} />
          <Fact icon={Sparkles} label="Action" body={card.action} />
          <Fact label="Why it works" body={card.why_works} />
          <Fact label="Success" body={card.success} />

          <button
            type="button"
            data-testid={`roundabout-trigger-got-it-${card.id}`}
            onClick={gotIt}
            disabled={busy}
            className="w-full mt-1 h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:bg-emerald-400"
          >
            {busy ? "Saving…" : "Got it"}
          </button>
        </div>
      )}
    </div>
  );
}

function Fact({ icon: Icon, label, body }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg px-2.5 py-2 border border-slate-200 dark:border-slate-800">
      <div className="flex items-center gap-1 mb-0.5">
        {Icon && <Icon className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />}
        <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">{label}</p>
      </div>
      <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{body}</p>
    </div>
  );
}
